import { CSSProperties, FC, PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';

import { ResizeObserver } from '@juggle/resize-observer';

import classNames from 'classnames';
import { Transition, TransitionStatus } from 'react-transition-group';
import useMeasure from 'react-use-measure';

const duration = 200;

const defaultStyle = {
   transition: `height ${duration}ms ease-in-out`,
   height: 0
};

interface Props extends PropsWithChildren {
   expanded?: boolean;
   onExpandToggle?: (state: boolean) => void;
   header?: ReactNode;
   overflow?: 'visible' | 'hidden';
   keepMounted?: boolean;
}

export const Expand: FC<Props> = ({ expanded, onExpandToggle, header, overflow = 'hidden', keepMounted = false, children }) => {
   const nodeRef = useRef(null);

   const [ref, { height: viewHeight }] = useMeasure({ polyfill: ResizeObserver });

   const [transitionStyles, setTransitionStyles] = useState<Record<TransitionStatus, CSSProperties | undefined>>(() => ({
      entering: { height: expanded ? 'auto' : `${viewHeight}px` },
      entered: { height: expanded ? 'auto' : `${viewHeight}px` },
      exiting: {},
      exited: {},
      unmounted: {}
   }));

   useEffect(() => {
      /* istanbul ignore next */
      if (viewHeight === 0) return;
      /* istanbul ignore next */
      setTransitionStyles((curr) => ({
         ...curr,
         entering: { height: `${viewHeight}px` },
         entered: { height: `${viewHeight}px` }
      }));
   }, [viewHeight]);

   const handleExpandToggle = () => onExpandToggle?.(!expanded);

   return (
      <div>
         {header && (
            <div data-testid="expand-header" role="presentation" onClick={handleExpandToggle} className={classNames('z-10', { 'cursor-pointer': !!onExpandToggle })}>
               {header}
            </div>
         )}
         <div className={overflow === 'hidden' ? 'overflow-hidden' : ''}>
            <Transition nodeRef={nodeRef} in={expanded} timeout={duration} mountOnEnter unmountOnExit={!keepMounted}>
               {(state) => (
                  <div
                     data-testid="expand-styles"
                     ref={nodeRef}
                     style={{
                        ...defaultStyle,
                        ...transitionStyles[state]
                     }}
                  >
                     <div ref={ref}>{children}</div>
                  </div>
               )}
            </Transition>
         </div>
      </div>
   );
};
